import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    DxButtonComponent,
    DxDataGridComponent,
    DxFormComponent,
    DxTemplateDirective,
} from 'devextreme-angular';
import {
    DxiButtonComponent,
    DxiColumnComponent,
    DxiItemComponent,
    DxoColumnChooserComponent,
    DxoExportComponent,
    DxoFilterRowComponent,
    DxoHeaderFilterComponent,
    DxoLoadPanelComponent,
    DxoPagerComponent,
    DxoPagingComponent,
    DxoPositionComponent,
    DxoScrollingComponent,
    DxoSearchComponent,
    DxoSelectionComponent,
    DxoSortingComponent,
    DxoToolbarComponent,
} from 'devextreme-angular/ui/nested';
import { SupplierStatus, SupplierGridViewDto, AuthService, PermissionType, AccessType } from '@services';
import { NgClass } from '@angular/common';
import { DxDataGridTypes } from 'devextreme-angular/ui/data-grid';
import EnumUtils from '../../../shared/enum-utils';
import { AddSupplierFormComponent } from './add-supplier-form/add-supplier-form.component';
import { EditSupplierFormComponent } from './edit-supplier-form/edit-supplier-form.component';
import { FilterPanelComponent } from '../../../components/library/filter-panel/filter-panel.component';
import { PhoneFormatPipe } from 'src/app/pipes/phone-format.pipe';
import { CapitalCasePipe } from '../../../pipes/capital-case.pipe';
import {
    FilterConfig,
    GridPageComponent,
} from '../../common/grid-page.component';
import { DATE_TIME_FORMAT } from 'src/app/shared/constants';
import { UserAvatarEmailComponent } from 'src/app/components/utils/user-avatar-email/user-avatar-email.component';

@Component({
    selector: 'app-suppliers',
    imports: [
        CommonModule,
        CapitalCasePipe,
        DxDataGridComponent,
        DxiColumnComponent,
        DxoHeaderFilterComponent,
        DxoScrollingComponent,
        DxButtonComponent,
        DxTemplateDirective,
        DxiItemComponent,
        DxoColumnChooserComponent,
        DxoExportComponent,
        DxoLoadPanelComponent,
        DxoSortingComponent,
        DxoToolbarComponent,
        DxoPagingComponent,
        DxoPagerComponent,
        DxoFilterRowComponent,
        DxFormComponent,
        FilterPanelComponent,
        AddSupplierFormComponent,
        EditSupplierFormComponent,
        DxiButtonComponent,
        DxoPositionComponent,
        DxoSearchComponent,
        DxoSelectionComponent,
        PhoneFormatPipe,
        NgClass,
        UserAvatarEmailComponent,
    ],
    templateUrl: './suppliers.component.html',
})
export class SuppliersComponent extends GridPageComponent<SupplierGridViewDto> {
    private authService = inject(AuthService);
    protected override exportName: string = 'Suppliers';
    protected offset: number = 100;
    public dateTimeFormat = DATE_TIME_FORMAT;

    isAddSupplierPopupOpened = false;
    isEditSupplierPopupOpened = false;
    supplierId: string;
    dataSource = this.dataSources.suppliers();
    supplierStatusOptions = EnumUtils.getEnumOptions(SupplierStatus);

    lastModifiedByFilter = this.userNameOrEmailFilter('lastModifiedBy');
    createdByFilter = this.userNameOrEmailFilter('createdBy');

    protected override filterConfig: FilterConfig[] = [
        { field: 'number', op: 'contains' },
        { field: 'name', op: 'contains' },
        { field: 'group', op: 'contains' },
        { field: 'address', op: 'contains' },
        { field: 'plant', op: 'contains' },
        { field: 'sac', op: 'contains' },
        { field: 'phone', op: 'contains' },
        { field: 'email', op: 'contains' },
        { field: 'status', op: '=' },
        {field: 'lastModifiedBy.name', op: 'contains', customFilter: this.lastModifiedByFilter},
        {field: 'lastModified', op: '>='},
        {field: 'createdBy.name', op: 'contains', customFilter: this.createdByFilter},
        {field: 'created', op: '>='},
    ];

    constructor() {
        super();
        this.init(SupplierGridViewDto);
    }

    protected getBadgeClass(status: SupplierStatus): string {
        switch (status) {
            case SupplierStatus.Inactive:
                return 'bg-secondary-subtle text-secondary';
            case SupplierStatus.Active:
                return 'bg-success-subtle text-success';
            default:
                return 'bg-info-subtle text-info';
        }
    }

    addSupplier() {
        this.isAddSupplierPopupOpened = true;
    }

    override onRowDblClick(e: DxDataGridTypes.RowDblClickEvent) {
        const { data } = e;

        this.supplierId = data.id;
        this.isEditSupplierPopupOpened = true;
    }

    protected async onSave() {
        await this.dataGrid.instance.refresh();
    }

    protected onDelete = ($event: any) => {
        this.dataGrid.instance.deleteRow($event.row.dataIndex);
    };

    override onEdit = (e: any) => {
        this.supplierId = e.row.data.id;
        this.isEditSupplierPopupOpened = true;
    };

    get canManage() {
        return this.authService.checkPermission(PermissionType.Suppliers, AccessType.Manage);
    }
}
